import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FILTER_TEMPLATES, FilterBarService } from '@modules/filters/filter-bar.service';
import { FilterBarComponent } from '@modules/filters/filter-bar/filter-bar.component';
import { UserSequenceGalleryService } from '@modules/gallery/services/dialogs/sliders/sequences/user-sequence-gallery.service';
import { SequenceListComponent } from '@modules/sequence/sequence-list/sequence-list.component';
import { SequencePreviewModeSegmentedComponent } from '@modules/sequence/sequence-preview-mode-segmented/sequence-preview-mode-segmented.component';
import { SequenceActionsService } from '@modules/sequence/services/sequence-actions.service';
import { SequencePreviewModeService } from '@modules/sequence/services/sequence-preview-mode.service';
import { ScrollHeaderTitleDirective } from '@modules/shared/directives/scroll-header-title.directive';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { PAGINATED_LIST_SOURCE } from '@modules/shared/paginated-list/paginated-list.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { FlattenedSequence } from '@pixacare/dtos';
import { FilterOperator, PaginationQuery } from '@pixacare/shared';
import { SequenceStorePaginationService } from '@services/sequence-store-pagination.service';
import { ContextualComponentMode } from '@shared/models/enums/contextual-component-mode.enum';
import { SequencePreviewMode } from '@shared/models/enums/sequence-preview-mode.enum';
import { sequenceFilterTemplates } from '@shared/models/filters/filter-template.config';
import { MediaClickedOutput } from '@shared/models/media-clicked-output';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { defaultPaginationContext } from '@shared/models/pagination/pagination-context.config';
import { JoinPipe } from '@shared/pipes/join.pipe';
import { selectDepartmentName } from '@shared/store/departments/departments.selectors';
import { selectHasLicenseWriteAccess } from '@shared/store/licenses/licenses.selectors';
import { selectClientCode, selectDepartmentId, selectQueryParams } from '@shared/store/router/router.selectors';
import { sequencesActions } from '@shared/store/sequences/sequences.actions';
import { selectIsSequenceStateLoaded } from '@shared/store/sequences/sequences.selectors';
import { HEADER_BREADCRUMB_SEPARATOR } from '@shared/utils/header-utils';
import { TuiButton, TuiTitle } from '@taiga-ui/core';
import { TuiHeader, TuiNavigation } from '@taiga-ui/layout';
import { combineLatest, distinctUntilChanged, map, Observable, Subscription } from 'rxjs';

@UntilDestroy()
@Component({
  templateUrl: './sequences.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FilterBarService,
    {
      provide: FILTER_TEMPLATES,
      useValue: sequenceFilterTemplates,
    },
    {
      provide: PAGINATED_LIST_SOURCE,
      useExisting: SequenceStorePaginationService,
    },
    UserSequenceGalleryService,
    JoinPipe,
  ],
  imports: [
    CommonModule,
    TuiHeader,
    TuiNavigation,
    TuiButton,
    LoaderComponent,
    FilterBarComponent,
    SequenceListComponent,
    ScrollHeaderTitleDirective,
    TranslocoDirective,
    TuiTitle,
    SequencePreviewModeSegmentedComponent,
  ],
})
export class SequencesComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly sequencePaginationService = inject(SequenceStorePaginationService);
  private readonly sequenceActionsService = inject(SequenceActionsService);
  private readonly userSequenceGalleryService = inject(UserSequenceGalleryService);
  private readonly filterBarService = inject(FilterBarService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly translocoService = inject(TranslocoService);
  private readonly sequencePreviewModeService = inject(SequencePreviewModeService);

  private loadSubscription: Subscription;

  hasLicenseWriteAccess$: Observable<boolean>;
  ContextualComponentMode = ContextualComponentMode;
  mode: ContextualComponentMode;

  clientCode: string = null;
  departmentId: number = null;
  isAppInitialized$: Observable<boolean>;
  isCollectionLoaded$: Observable<boolean> = null;
  readonly params$ = combineLatest({
    clientCode: this.store.select(selectClientCode),
    departmentId: this.store.select(selectDepartmentId),
  }).pipe(untilDestroyed(this));

  actions: MenuAction<FlattenedSequence>[];

  sequencePreviewMode: SequencePreviewMode = this.sequencePreviewModeService.getPreviewMode();

  get headerTitle$(): Observable<string> {
    return this.store
      .select(selectDepartmentName(this.clientCode, this.departmentId))
      .pipe(
        map((departmentName) =>
          this.mode === ContextualComponentMode.USER ? this.translocoService.translate('Ma galerie') : departmentName,
        ),
      );
  }

  get headerSubtitle(): string {
    return this.mode === ContextualComponentMode.DEPARTMENT
      ? this.translocoService.translate('Galerie du groupe')
      : null;
  }

  get scrollHeaderTitle$(): Observable<string> {
    return combineLatest([this.headerTitle$, this.filterBarService.selectedTabValue$]).pipe(
      map(([headerTitle, filterTabValue]) =>
        this.joinPipe.transform(
          [
            headerTitle,
            ...(this.headerSubtitle ? [this.headerSubtitle] : []),
            ...(filterTabValue ? [filterTabValue] : []),
          ],
          HEADER_BREADCRUMB_SEPARATOR,
        ),
      ),
    );
  }

  ngOnInit(): void {
    this.resetSequences();
    this.filterBarService.init();
    this.params$.subscribe(({ clientCode, departmentId }) => {
      this.clientCode = clientCode;
      this.departmentId = departmentId;

      if (this.clientCode) {
        this.mode = this.departmentId ? ContextualComponentMode.DEPARTMENT : ContextualComponentMode.USER;

        this.hasLicenseWriteAccess$ = this.store.select(selectHasLicenseWriteAccess);

        this.actions = this.sequenceActionsService.getActions();
      }
    });

    this.store
      .select(selectQueryParams)
      .pipe(
        map(({ create }) => create),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((create) => {
        if (create) {
          this.router.navigate([], {
            queryParams: { create: null },
            queryParamsHandling: 'merge',
          });
          this.addSequence();
        }
      });

    this.loadSequences();
  }

  setPreviewMode(mode: SequencePreviewMode): void {
    this.sequencePreviewMode = mode;
    this.resetSequences();
    this.loadSequences();
  }

  resetSequences(): void {
    this.store.dispatch(sequencesActions.resetSequencesState());
  }

  loadSequences(): void {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = combineLatest([this.filterBarService.filteredSearch$, this.params$])
      .pipe(untilDestroyed(this))
      .subscribe(([{ search, filters }, { clientCode, departmentId }]) => {
        this.sequencePaginationService.load(clientCode, {
          query: new PaginationQuery({
            orderBy: ['created_on|desc'],
            filter: [
              ...filters,
              ...(departmentId
                ? [{ prop: 'department_id', op: FilterOperator.EQUAL, val: departmentId.toString() }]
                : []),
            ],
            search,
            size: defaultPaginationContext.countPerPage,
          }),
          reset: true,
        });
      });

    this.isCollectionLoaded$ = this.store.select(selectIsSequenceStateLoaded);
  }

  addSequence(): void {
    this.sequenceActionsService
      .addSequence({
        clientCode: this.clientCode,
        ...(this.departmentId && { departmentIds: [this.departmentId] }),
      })
      .subscribe((created) => {
        if (created) {
          this.filterBarService.clear();
        }
      });
  }

  openSequenceMediaGallery({ clientCode, sequenceId, mediaIdx }: MediaClickedOutput): void {
    this.userSequenceGalleryService.openGallery(clientCode, sequenceId, mediaIdx);
  }
}
